// One lift card for the customer portal (Session AG Item 5): the lift's compliance status, next
// service + LOLER due, and an expandable section with the LOLER inspection detail, open items and
// recent visits. On the site detail page it also offers the LOLER certificate download (a secure,
// audited proxy that streams the PDF from Drive; the raw Drive URL never reaches the browser). Shared
// by the site detail page; plain English, no pricing/engineer names/internal notes.
import { useState } from "react";
import { api } from "../../lib/api";
import type { PortalLift, PortalComplianceStatus } from "../../lib/api";

// Compliance label -> the locked six-variant pill system (Session ACG: office and portal
// share one badge language; see .ele-pill in index.css).
const COMPLIANCE_PILL: Record<string, string> = {
  "Up to date": "ele-pill--green",
  "Due soon": "ele-pill--amber",
  Overdue: "ele-pill--red",
};

export const STATUS_DOT: Record<PortalComplianceStatus, string> = {
  green: "bg-ele-green",
  amber: "bg-amber-400",
  red: "bg-ele-danger",
};

export function StatusDot({ status }: { status: PortalComplianceStatus }) {
  return <span className={`inline-block h-2.5 w-2.5 rounded-full ${STATUS_DOT[status]}`} aria-hidden="true" />;
}

export function PortalLiftCard({ lift, showLolerCert = false }: { lift: PortalLift; showLolerCert?: boolean }) {
  const [open, setOpen] = useState(false);
  const pill = COMPLIANCE_PILL[lift.complianceLabel] ?? "ele-pill--grey";
  const canExpand = lift.recentVisits.length > 0 || lift.openDefects.length > 0 || lift.loler.state !== "unknown";
  return (
    <div className="rounded-ele border border-white/10 bg-[rgba(255,255,255,0.04)] p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <StatusDot status={lift.complianceStatus} />
          <span className="font-heading text-lg text-ele-white">{lift.ref}</span>
          <span className="text-xs text-ele-body">{lift.assetType}</span>
        </div>
        <span className={`ele-pill ${pill}`}>{lift.complianceLabel}</span>
      </div>
      <div className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
        <p>
          <span className="text-ele-body">Next service: </span>
          <span className="text-ele-white">{lift.nextServiceDisplay ?? "To be scheduled"}</span>
        </p>
        <p>
          <span className="text-ele-body">Next LOLER inspection: </span>
          {/* Session ACR: the LOLER clock belongs to the examining body, so care equipment with
              no recorded examination says so honestly (amber), never "to be scheduled"; a
              fixed-height item carries no examination requirement at all. Lifts unchanged. */}
          {lift.equipment && lift.examState === "awaiting" ? (
            <span className="text-amber-400">Awaiting first examination record</span>
          ) : lift.equipment && lift.examState === "not_required" ? (
            <span className="text-ele-body">Not required</span>
          ) : (
            <span className="text-ele-white">{lift.lolerDueDisplay ?? "To be scheduled"}</span>
          )}
        </p>
      </div>
      {showLolerCert && lift.hasLolerCert && (
        <a
          href={api.portal.lolerCertificateHref(lift.assetId)}
          className="mt-3 inline-block rounded-ele border border-ele-green px-3 py-1.5 font-menu text-xs tracking-wide text-ele-green transition hover:bg-ele-green/10"
        >
          Download LOLER certificate
        </a>
      )}
      {/* Session AUD (Item 6): report a fault on THIS lift. Never for an entrapment or an emergency: the
          page it opens says to phone first. A plain link, not a router Link, so the card renders with or
          without a router around it. */}
      <a
        href={`/portal/report-fault?lift=${encodeURIComponent(lift.assetId)}`}
        className="mt-3 inline-block font-menu text-xs tracking-wide text-ele-green hover:underline"
      >
        Report a fault on this lift
      </a>
      {canExpand && (
        <button onClick={() => setOpen((o) => !o)} className="mt-2 block text-xs text-ele-green hover:underline">
          {open ? "Hide details" : "View details"}
        </button>
      )}
      {open && (
        <div className="mt-3 space-y-4 border-t border-white/10 pt-3">
          <div>
            <p className="font-menu text-[10px] uppercase tracking-wider text-ele-blue">Safety inspection (LOLER)</p>
            <p className="text-sm text-ele-white">{lift.loler.label}</p>
            <div className="mt-1 grid gap-0.5 text-xs text-ele-body sm:grid-cols-2">
              {lift.loler.lastInspectionDisplay && <span>Last inspection: {lift.loler.lastInspectionDisplay}</span>}
              {lift.loler.nextDueDisplay && <span>Next due: {lift.loler.nextDueDisplay}</span>}
              {lift.loler.inspector && <span>Inspected by: {lift.loler.inspector}</span>}
            </div>
          </div>

          {lift.openDefects.length > 0 && (
            <div>
              <p className="font-menu text-[10px] uppercase tracking-wider text-ele-blue">Open items</p>
              <ul className="mt-1 space-y-1 text-sm">
                {lift.openDefects.map((d, i) => (
                  <li key={i} className="flex items-start justify-between gap-3">
                    <span className="text-ele-white">{d.description}</span>
                    <span className="shrink-0 text-xs text-ele-body">
                      {d.severityLabel}
                      {d.raisedDisplay ? ` · ${d.raisedDisplay}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {lift.recentVisits.length > 0 && (
            <div>
              <p className="font-menu text-[10px] uppercase tracking-wider text-ele-blue">Recent visits</p>
              <ul className="mt-1 space-y-1 text-sm">
                {lift.recentVisits.map((v, i) => (
                  <li key={i} className="flex items-start justify-between gap-3">
                    <span className="text-ele-white">
                      {v.dateDisplay} <span className="text-ele-body">{v.typeLabel}</span>
                    </span>
                    <span className="shrink-0 text-xs text-ele-body">{v.outcome}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
